import { useCallback } from 'react';
import { deleteDoc, Timestamp as FirestoreTimestamp } from 'firebase/firestore';
import { addActivityLog } from '../../../api/documentsAndGoals';
import trackBehaviorDataChanges from './helpers/changeTracking';
import {
  useCheckboxChangeHandler,
  useTimeChangeHandler,
  useNumericFieldChangeHandler,
  useFieldChangeHandler,
  useAbcChangeHandler,
  usePromptingChangeHandler,
} from './useBehaviorHandlers/basicHandlers';
import useResetDataPoints from './useBehaviorHandlers/reset';
import { safeJsonBtoa } from '../../../utils/encodingUtils';

const useBehaviorDataHandlers = ({
  setDataPoints,
  selectedDataPoint,
  onSuccess,
  onClose,
  studentId,
  ACM,
  typeOfUser,
  FirestoreDB,
  queryClient,
  user,
  dataPoints,
  Timestamp,
  dayjs,
  selectedDate,
  editStudentQuickTrackEntry,
  addDoc,
  collection,
  doc,
  videoId,
  showToastMessage,
  isPreRegistered,
  setIsDisabled,
  setIsDeleting,
  promptingType,
  fromSessionNotes = false,
  dataEntryRef = null,
  behaviorId = null,
}) => {
  const formatCustomDataPayload = (customSections = []) => {
    if (!Array.isArray(customSections) || customSections.length === 0) {
      return {};
    }

    return customSections.reduce((acc, section) => {
      if (!section?.docId) {
        return acc;
      }

      let formattedQuestions = {};
      if (Array.isArray(section.questions)) {
        formattedQuestions = section.questions.reduce((questionAcc, question) => {
          if (!question?.id) {
            return questionAcc;
          }
          questionAcc[question.id] =
            question?.answer !== undefined ? question.answer : '';
          return questionAcc;
        }, {});
      } else if (
        section?.questions &&
        typeof section.questions === 'object'
      ) {
        formattedQuestions = { ...section.questions };
      }

      acc[section.docId] = formattedQuestions;
      return acc;
    }, {});
  };
  const handleDelete = useCallback(
    async (behaviorId) => {
      setIsDeleting(true);
      const collectionName = isPreRegistered(studentId)
        ? 'preRegisteredUsers'
        : ACM[typeOfUser]?.isTeacher || ACM[typeOfUser]?.isStudent
          ? 'studentsList'
          : 'patientsList';

      try {
        if (selectedDataPoint) {
          const dataEntryId = selectedDataPoint.id;
          const goalDocRef = doc(
            FirestoreDB,
            collectionName,
            studentId,
            'behaviors',
            behaviorId,
            'goalData',
            dataEntryId
          );

          const formatDate = (date) => {
            if (!date) return '-';

            if (typeof date === 'string') return date;

            if (date instanceof Date) {
              return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              });
            }
            return '-';
          };

          const formattedDate = formatDate(
            selectedDate || selectedDataPoint?.date
          );

          const activityEntry = {
            actionPerformed: `Data entry from ${formattedDate} deleted`,
            editedBy: user?.uid,
            typeOfActivity: 'Delete Data Entry',
            createdAt: FirestoreTimestamp.now(),
          };

          const encodedActivityData = safeJsonBtoa(activityEntry);

          await addActivityLog(
            collectionName,
            studentId,
            'behaviors',
            behaviorId,
            encodedActivityData
          );

          await deleteDoc(goalDocRef);
          showToastMessage('delete');
          await queryClient.invalidateQueries({
            queryKey: ['fetchBehaviorData', user?.uid, typeOfUser],
            refetchType: 'all',
          });

          if (onSuccess) {
            await onSuccess();
          }

          onClose();
        }
      } catch (error) {
        console.error('Error deleting data:', error);
        showToastMessage('error');
      } finally {
        setIsDeleting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      setIsDeleting,
      studentId,
      isPreRegistered,
      ACM,
      typeOfUser,
      selectedDataPoint,
      FirestoreDB,
      queryClient,
      user,
      onSuccess,
      onClose,
      showToastMessage,
      doc,
      selectedDate,
      addActivityLog,
    ]
  );

  const handleCheckboxChange = useCheckboxChangeHandler(setDataPoints);
  const handleTimeChange = useTimeChangeHandler(setDataPoints);
  const handleNumericFieldChange = useNumericFieldChangeHandler(setDataPoints);
  const handleFieldChange = useFieldChangeHandler(setDataPoints);
  const handleAbcChange = useAbcChangeHandler(setDataPoints);
  const handlePromptingChange = usePromptingChangeHandler(setDataPoints);
  const resetDataPoints = useResetDataPoints(setDataPoints);

  const handleSubmit = useCallback(
    async (behaviorId) => {
      setIsDisabled(true);
      let isSuccess = false;
      const collectionName = isPreRegistered(studentId)
        ? 'preRegisteredUsers'
        : ACM[typeOfUser]?.isTeacher || ACM[typeOfUser]?.isStudent
          ? 'studentsList'
          : 'patientsList';

      const firestoreTimestamp = Timestamp.fromDate(
        dayjs(selectedDate).toDate()
      );
      const currentTime = new Date();

      try {
        const formData = dataPoints
          ?.filter((dp) => dp !== null)
          ?.map((dataPoint) => ({
            id: dataPoint?.id || doc(collection(FirestoreDB, 'temp')).id,
            frequency: dataPoint.frequency || 0,
            duration: dataPoint.duration || 0,
            severity: dataPoint.severity || 0,
            intensity: dataPoint.intensity || 0,
            accuracy: dataPoint.accuracy || 0,
            independence: dataPoint.independence || 0,
            yesNo: dataPoint.yesNo ?? '',
            abc_data: dataPoint?.selectedOptions.includes('Add ABC Data')
              ? {
                antecedent: dataPoint?.abcData?.antecedent || '',
                behavior: dataPoint?.abcData?.behavior || '',
                consequence: dataPoint?.abcData?.consequence || '',
                setting: dataPoint?.abcData?.setting || '',
                notes: dataPoint?.abcData?.notes || '',
              }
              : '',
            phase_change: dataPoint?.selectedOptions.includes(
              'Add Phase Change Line'
            )
              ? dataPoint?.phaseChange || ''
              : '',
            event: dataPoint?.selectedOptions.includes('Add Event Line')
              ? dataPoint?.eventLine || ''
              : '',
            preceding_behavior: dataPoint?.selectedOptions.includes('Add Preceding Behavior')
              ? dataPoint?.precedingBehavior || ''
              : '',
            settingLocation: dataPoint?.selectedOptions.includes(
              'Add Setting/Location'
            )
              ? dataPoint?.settingLocation || ''
              : '',
            notes: dataPoint?.notes || '',
            timeOfCollection: dataPoint?.selectedOptions.includes(
              'Add Time of Collection'
            )
              ? dataPoint?.timeOfCollection || ''
              : '',
            timeOfCollectionSpecific: dataPoint?.selectedOptions.includes(
              'Add Time of Collection'
            )
              ? dataPoint?.timeOfCollectionSpecific || ''
              : '',
            // Staff present information
            additionalStaff: dataPoint?.selectedOptions.includes(
              'Add Staff Present Information'
            )
              ? dataPoint?.additionalStaff || false
              : undefined,
            numberOfStaffs: dataPoint?.selectedOptions.includes(
              'Add Staff Present Information'
            )
              ? dataPoint?.numberOfStaffs || 0
              : undefined,
            // Prompting level
            promptingLevel: dataPoint?.selectedOptions.includes(
              'Add Prompting Level'
            )
              ? dataPoint?.promptingLevel || ''
              : '',
            // Session summary
            sessionSummary: dataPoint?.selectedOptions.includes(
              'Add Session Summary'
            )
              ? dataPoint?.sessionSummary || ''
              : '',
            // Group size
            groupSize: dataPoint?.selectedOptions.includes('Add Group Size')
              ? dataPoint?.groupSize === 'Other' && dataPoint?.customGroupSize
                ? `Group of ${dataPoint.customGroupSize}`
                : dataPoint?.groupSize || ''
              : '',
            customGroupSize: dataPoint?.selectedOptions.includes('Add Group Size')
              ? dataPoint?.customGroupSize || ''
              : '',
            // Session length
            sessionLength: dataPoint?.selectedOptions.includes(
              'Add Session Length'
            )
              ? dataPoint?.sessionLength || ''
              : '',
            // CPT codes
            cptCodes: dataPoint?.selectedOptions.includes('Add CPT Code')
              ? dataPoint?.cptCodes || ''
              : '',
            prompting: dataPoint?.prompting || {},
            currentGoal: dataPoint?.currentGoal || '',
            currentTime: currentTime.toISOString(),
            interventions: dataPoint?.interventions || [],
            customData: (() => {
              if (
                dataPoint?.customSection &&
                Array.isArray(dataPoint.customSection)
              ) {
                return dataPoint.customSection.map((section) => ({
                  docId: section.docId,
                  definition: section.definition || '',
                  title: section.title || '',
                  questions: section.questions || [],
                }));
              }
              return [];
            })(),
            //need to check this
            // customData: dataPoint?.customSection || [],
            ...(videoId && { videoId }),
          }));

        if (selectedDataPoint) {
          console.log('Editing existing data entry', selectedDataPoint);

          // Track changes between existing and new data
          const changes = trackBehaviorDataChanges(selectedDataPoint, formData);

          const result = await editStudentQuickTrackEntry(
            user?.uid,
            typeOfUser,
            studentId,
            behaviorId,
            selectedDataPoint?.id,
            safeJsonBtoa(formData)
          );

          console.log('editStudentQuickTrackEntry result:', result);
          if (!result) {
            console.error('Edit failed: No message returned');
            showToastMessage('Data entry upload failed!');
            return false;
          }

          // Capture dataEntryId when editing from session notes
          if (fromSessionNotes && dataEntryRef && behaviorId) {
            dataEntryRef.current = dataEntryRef.current || {};

            dataEntryRef.current[behaviorId] = selectedDataPoint?.id;
          }

          // Show success toast for edit
          showToastMessage('success');

          // Add activity log entry for edit with change details
          const activityEntry = {
            actionPerformed: 'Edited Data Entry',
            editedBy: user?.uid,
            typeOfActivity: 'Edited Data Entry',
            createdAt: FirestoreTimestamp.now(),
            changes: changes.length > 0 ? changes : [],
          };

          const encodedActivityData = safeJsonBtoa(activityEntry);

          await addActivityLog(
            collectionName,
            studentId,
            'behaviors',
            behaviorId,
            encodedActivityData
          );

          await queryClient.invalidateQueries({
            queryKey: ['fetchBehaviorData', user?.uid, typeOfUser],
            refetchType: 'all',
          });

          // Reset form and close dialog
          isSuccess = true;
          resetDataPoints(promptingType);
          try {
            if (onSuccess) {
              await onSuccess();
            }
            if (onClose) {
              onClose();
            }
          } catch (callbackError) {
            console.error('Error in success callbacks:', callbackError);
            setIsDisabled(false);
          }
          return isSuccess;
        }
        const addResult = await addDoc(
          collection(
            FirestoreDB,
            collectionName,
            studentId,
            'behaviors',
            behaviorId,
            'goalData'
          ),
          {
            info: safeJsonBtoa(formData),
            createdAt: firestoreTimestamp,
            isEncrypted: false,
          }
        );

        // Capture dataEntryId when submitting from session notes
        if (fromSessionNotes && dataEntryRef && behaviorId) {
          dataEntryRef.current = dataEntryRef.current || {};
          dataEntryRef.current[behaviorId] = addResult.id;
        }

        // Show success toast for new data entry
        showToastMessage('success');

        // Add activity log entry for new data entry
        const activityEntry = {
          actionPerformed: 'Added new data entry',
          editedBy: user?.uid,
          typeOfActivity: 'New Data Entry',
          createdAt: FirestoreTimestamp.now(),
        };

        const encodedActivityData = btoa(JSON.stringify(activityEntry));

        await addActivityLog(
          collectionName,
          studentId,
          'behaviors',
          behaviorId,
          encodedActivityData
        );
        await queryClient.invalidateQueries({
          queryKey: ['fetchBehaviorData', user?.uid, typeOfUser],
          refetchType: 'all',
        });

        // Reset form for new entry (don't close dialog)
        isSuccess = true;
        resetDataPoints(promptingType);
        try {
          if (onSuccess) {
            await onSuccess();
          }
          // Don't close dialog for new entries - let user add more data
        } catch (callbackError) {
          console.error('Error in success callbacks:', callbackError);
          setIsDisabled(false);
        }
      } catch (error) {
        console.error('Error saving data entry (QuickTrack):', error);
        if (error && error.stack) {
          console.error(error.stack);
        }
        showToastMessage('error');
      } finally {
        setIsDisabled(false);
      }

      return isSuccess;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      setIsDisabled,
      studentId,
      isPreRegistered,
      ACM,
      typeOfUser,
      Timestamp,
      dayjs,
      selectedDate,
      dataPoints,
      FirestoreDB,
      collection,
      doc,
      videoId,
      selectedDataPoint,
      editStudentQuickTrackEntry,
      user,
      queryClient,
      showToastMessage,
      addDoc,
      onSuccess,
      onClose,
      resetDataPoints,
      fromSessionNotes,
      dataEntryRef,
      behaviorId,
    ]
  );

  const handleAddDataPoint = useCallback(() => {
    const createInitialDataPoint = (promptingType) => {
      return {
        frequency: '',
        duration: '',
        severity: '',
        intensity: '',
        accuracy: '',
        independence: '',
        yesNo: '',
        selectedOptions: [],
        abcData: {
          antecedent: '',
          behavior: '',
          consequence: '',
          setting: '',
          notes: '',
        },
        settingLocation: '',
        phaseChange: '',
        eventMarker: '',
        numberOfStaffs: 0,
        promptingLevel: '',
        timeOfCollection: '',
        timeOfCollectionSpecific: '',
        sessionSummary: '',
        groupSize: '',
        sessionLength: '',
        cptCodes: '',
        additionalStaff: false,
        prompting: promptingType === 'P+ & P-'
          ? { pMinus: '', pPlus: '' }
          : promptingType === 'IVP, DVP, PVP, GP, PP'
            ? { I: '', VP: '', IVP: '', DVP: '', PVP: '', GP: '', PP: '' }
            : {},
        currentGoal: '',
        interventions: [],
        customSection: [],
      };
    };

    setDataPoints((prev) => [
      ...prev,
      createInitialDataPoint(promptingType),
    ]);
  }, [promptingType, setDataPoints]);

  return {
    handleDelete,
    handleCheckboxChange,
    handleTimeChange,
    handleNumericFieldChange,
    handleFieldChange,
    handleAbcChange,
    handlePromptingChange,
    handleSubmit,
    resetDataPoints,
    handleAddDataPoint,
  };
};

export default useBehaviorDataHandlers;
